// @ts-nocheck
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { GradientButton } from "./GradientButton";
import { LucideIcon } from "lucide-react";

interface HeaderAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: "default" | "outline" | "ghost" | "destructive";
  className?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  leadingAction?: HeaderAction;
  primaryAction?: HeaderAction;
  secondaryActions?: HeaderAction[];
  children?: React.ReactNode;
}

/**
 * PageHeader — the standard page header card used across all list pages.
 *
 * Renders a white card with title, optional subtitle, and action buttons.
 * On mobile, button labels are hidden and shown in tooltips.
 */
export function PageHeader({ title, subtitle, leadingAction, primaryAction, secondaryActions = [], children }: PageHeaderProps) {
  return (
    <Card className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-xl shadow-sm">
      <div className="p-2.5 px-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex items-center gap-3">
            {leadingAction && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={leadingAction.onClick}
                      variant={leadingAction.variant || "ghost"}
                      className={leadingAction.className || "shrink-0"}
                      size="icon"
                    >
                      {leadingAction.icon && <leadingAction.icon className="w-4 h-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{leadingAction.label}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-slate-900">{title}</h1>
              {subtitle && <p className="text-xs text-slate-500 hidden sm:block">{subtitle}</p>}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {primaryAction && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <GradientButton
                      onClick={primaryAction.onClick}
                      className={primaryAction.className}
                      size="sm"
                    >
                      {primaryAction.icon && <primaryAction.icon className="w-4 h-4 sm:mr-2" />}
                      <span className="hidden sm:inline">{primaryAction.label}</span>
                    </GradientButton>
                  </TooltipTrigger>
                  <TooltipContent className="sm:hidden">{primaryAction.label}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {secondaryActions.map((action, i) => (
              <TooltipProvider key={i}>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={action.onClick}
                      variant={action.variant || "outline"}
                      className={action.className || "border-primary text-primary hover:bg-primary hover:text-white"}
                      size="sm"
                    >
                      {action.icon && <action.icon className="w-4 h-4 sm:mr-2" />}
                      <span className="hidden sm:inline">{action.label}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="sm:hidden">{action.label}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            {children}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default PageHeader;
